/**
 * lib/ninetailed/mapEntryIds.ts
 *
 * Pure mapper: pairs each Ninetailed field id (`ntExperienceId` / `ntAudienceId`)
 * back to its Contentful ENTRY `sys.id`, so the preview widget's "Go to editor"
 * callbacks — which only receive the Ninetailed ids — can deep-link into the
 * correct Contentful entry editor.
 *
 * Why this exists: the `ExperienceConfiguration` / `ExposedAudienceDefinition`
 * objects the widget hands to `onOpenExperienceEditor` / `onOpenAudienceEditor`
 * expose only `id` = `nt_experience_id` / `nt_audience_id` (a Ninetailed custom
 * field value), never the Contentful `sys.id` required to open the entry. We fetch
 * both together in the catalog query and build the lookup here.
 *
 * Defensive by design (mirrors `mapExperiences`/`mapAudiences`): on ANY unexpected
 * input it returns empty maps and NEVER throws. Entries missing either id are
 * skipped. Preview-only — never on the render path.
 */

export interface NinetailedEntryIdMaps {
  /** `nt_experience_id` -> Contentful experience entry `sys.id`. */
  experienceEntryIdByNtId: Record<string, string>;
  /** `nt_audience_id` -> Contentful audience entry `sys.id`. */
  audienceEntryIdByNtId: Record<string, string>;
}

type RawEntry = {
  sys?: { id?: string | null } | null;
  ntExperienceId?: string | null;
  ntAudienceId?: string | null;
} | null;

function toItemsArray(input: unknown): Array<RawEntry> | null {
  if (Array.isArray(input)) return input as Array<RawEntry>;
  if (input && Array.isArray((input as { items?: unknown }).items)) {
    return (input as { items: Array<RawEntry> }).items;
  }
  return null;
}

function buildMap(
  input: unknown,
  ntIdField: 'ntExperienceId' | 'ntAudienceId',
): Record<string, string> {
  const items = toItemsArray(input);
  if (!items) return {};

  return items.reduce<Record<string, string>>((acc, raw) => {
    const ntId = raw?.[ntIdField];
    const sysId = raw?.sys?.id;
    if (typeof ntId === 'string' && typeof sysId === 'string') {
      acc[ntId] = sysId;
    }
    return acc;
  }, {});
}

/**
 * Build the `nt id -> Contentful sys.id` lookups from the raw catalog collections.
 * Returns empty maps for any missing/malformed input and never throws.
 */
export function mapEntryIds(
  experienceInput: unknown,
  audienceInput: unknown,
): NinetailedEntryIdMaps {
  try {
    return {
      experienceEntryIdByNtId: buildMap(experienceInput, 'ntExperienceId'),
      audienceEntryIdByNtId: buildMap(audienceInput, 'ntAudienceId'),
    };
  } catch {
    return { experienceEntryIdByNtId: {}, audienceEntryIdByNtId: {} };
  }
}
